<template>
  <div class="field-selector">
    <n-spin :show="loading">
      <div v-if="error" class="error-msg">{{ error }}</div>

      <div v-else-if="fields.length === 0" class="empty">
        Nincs elérhető parcella.
      </div>

      <div v-else class="field-list">
        <div
          v-for="field in fields"
          :key="field.id"
          class="field-row"
          :class="{ 'field-row--active': activeFieldId === field.id }"
          @click="handleSelect(field)"
        >
          <div class="field-row__main">
            <span class="field-name">{{ field.name }}</span>
          </div>

          <!-- Zoom-to button — only shown when a boundary exists -->
          <n-button
            v-if="field.extent"
            quaternary
            circle
            size="tiny"
            class="zoom-btn"
            title="Ugrás a parcellához"
            @click.stop="$emit('zoom-to', field.extent!)"
          >
            <template #icon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2.2"
                   stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="8"/>
                <line x1="12" y1="2" x2="12" y2="6"/>
                <line x1="12" y1="18" x2="12" y2="22"/>
                <line x1="2" y1="12" x2="6" y2="12"/>
                <line x1="18" y1="12" x2="22" y2="12"/>
              </svg>
            </template>
          </n-button>
        </div>
      </div>
    </n-spin>
  </div>
</template>

<script setup lang="ts">
import { NSpin, NButton } from 'naive-ui'
import type { FieldSummary } from '@/composables/useFarms'

defineProps<{
  fields: FieldSummary[]
  loading: boolean
  error: string | null
  activeFieldId: number | null
}>()

const emit = defineEmits<{
  /** User clicked a field row — parent should call selectField(id) */
  (e: 'select', field: FieldSummary): void
  /** User clicked the zoom button — extent is [minX, minY, maxX, maxY] (3857) */
  (e: 'zoom-to', extent: [number, number, number, number]): void
}>()

function handleSelect(field: FieldSummary) {
  emit('select', field)
  if (field.extent) emit('zoom-to', field.extent)
}
</script>

<style scoped>
.field-selector {
  color: #cdd6f4;
}

.empty {
  font-size: 12px;
  color: #a6adc8;
  padding: 12px 4px;
}

.error-msg {
  color: #f38ba8;
  font-size: 12px;
  padding: 8px 4px;
}

.field-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  border: 1px solid transparent;
}

.field-row:hover {
  background: rgba(166, 227, 161, 0.08);
}

.field-row--active {
  background: rgba(166, 227, 161, 0.15);
  border-color: rgba(166, 227, 161, 0.35);
}

.field-row__main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.field-name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.zoom-btn {
  color: #a6e3a1 !important;
  flex-shrink: 0;
}
.zoom-btn:hover {
  color: #94e2d5 !important;
}
</style>
